import { rotatedSize, transformStrokes, type Size } from "./geometry";
import { computeMask } from "./masking";
import { renderBrick } from "./rendering";
import type { BrickRegion, MasterImage, Pixels, Rect } from "./types";

export const loadImage = (url: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Could not load ${url}`));
    img.src = url;
  });

export const drawRotated = (
  source: CanvasImageSource,
  size: Size,
  rotation: number,
  rect: Rect,
  scale: number
): HTMLCanvasElement => {
  const bounds = rotatedSize(size.width, size.height, rotation);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(rect.width * scale));
  canvas.height = Math.max(1, Math.round(rect.height * scale));
  const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
  ctx.imageSmoothingQuality = "high";
  ctx.scale(scale, scale);
  ctx.translate(-rect.x, -rect.y);
  ctx.translate(bounds.width / 2, bounds.height / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.translate(-size.width / 2, -size.height / 2);
  ctx.drawImage(source, 0, 0, size.width, size.height);
  return canvas;
};

export const canvasPixels = (canvas: HTMLCanvasElement): Pixels => {
  const { data } = canvas
    .getContext("2d", { willReadFrequently: true })!
    .getImageData(0, 0, canvas.width, canvas.height);
  return { width: canvas.width, height: canvas.height, data };
};

export const pixelsToPngBlob = (pixels: Pixels) => {
  const canvas = document.createElement("canvas");
  canvas.width = pixels.width;
  canvas.height = pixels.height;
  canvas
    .getContext("2d")!
    .putImageData(new ImageData(new Uint8ClampedArray(pixels.data), pixels.width, pixels.height), 0, 0);
  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Could not encode PNG"))), "image/png")
  );
};

export const renderRegionPng = (original: CanvasImageSource, masterImage: MasterImage, region: BrickRegion) => {
  if (masterImage.pxPerMm == null) throw new Error("Detect bricks before applying the master image");
  const pixels = canvasPixels(drawRotated(original, masterImage, masterImage.rotation, region, 1));
  const rect = { x: 0, y: 0, width: pixels.width, height: pixels.height };
  const alpha = computeMask({
    image: pixels,
    rect,
    tolerance: region.tolerance ?? masterImage.tolerance,
    strokes: transformStrokes(region.strokes, 1, -region.x, -region.y),
  });
  return pixelsToPngBlob(renderBrick({ image: pixels, rect, alpha, sourcePxPerMm: masterImage.pxPerMm }));
};
